import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { api } from '../../../utils/variables'
import { API_ENDPOINTS } from '../constants'
import { getAuthHeaders, getPostHeaders, getCountryCode } from '../utils'

export const useKeywordTrackingAPI = (item_id, initialCountriesData = []) => {
  const [countriesData, setCountriesData] = useState(initialCountriesData)
  const [predefinedKeywords, setPredefinedKeywords] = useState([])
  const [keywordsLoading, setKeywordsLoading] = useState(true)
  const [trackedApp, setTrackedApp] = useState(null)
  const [trackedKeywordSummary, setTrackedKeywordSummary] = useState(null)
  const [keywordTrackingData, setKeywordTrackingData] = useState(null)
  const [gameData, setGameData] = useState([])
  const [addingKeyword, setAddingKeyword] = useState(false)
  const [notFoundError, setNotFoundError] = useState(false)

  // Initialize keywordsLoading to true when component mounts
  useEffect(() => {
    setKeywordsLoading(true);
  }, []);

  // Fetch countries only if we don't have initial data
  const fetchCountries = useCallback(async () => {
    // Skip if we already have countries data
    if (countriesData.length > 0) {
      return;
    }

    try {
      const res = await axios.get(`${api}${API_ENDPOINTS.countries}`)
      if (res.data.status === 1) {
        setCountriesData(res.data.data)
      }
    } catch (err) {
      console.error("Failed to fetch countries:", err)
    }
  }, [countriesData.length])

  // Fetch predefined keywords
  const fetchPredefinedKeywords = useCallback(async () => {
    try {
      const res = await axios.get(`${api}${API_ENDPOINTS.predefinedKeywords}`, {
        headers: getAuthHeaders(),
      });

      if (res.data && res.data.keywords) {
        setPredefinedKeywords(res.data.keywords);
        setKeywordsLoading(false);
        return res.data.keywords;
      } else {
        // Fallback predefined keywords based on the design
        const fallbackKeywords = [
          "VR Shooter",
          "Survival Adventure",
          "First-person VR Puzzle",
          "Sports VR",
          "VR Tennis",
          "VR Gaming",
          "Adventure",
          "Puzzle",
          "Action",
          "Strategy"
        ];
        setPredefinedKeywords(fallbackKeywords);
        setKeywordsLoading(false);
        return fallbackKeywords;
      }
    } catch (e) {
      console.error("Failed to fetch predefined keywords:", e);

      // Fallback predefined keywords based on the design
      const fallbackKeywords = [
        "VR Shooter",
        "Survival Adventure",
        "First-person VR Puzzle",
        "Sports VR",
        "VR Tennis",
        "VR Gaming",
        "Adventure",
        "Puzzle",
        "Action",
        "Strategy"
      ];
      setPredefinedKeywords(fallbackKeywords);
      setKeywordsLoading(false);
      return fallbackKeywords;
    }
  }, [])

  // Fetch tracked app summary
  const fetchTrackedApp = useCallback(async () => {
    try {
      const res = await axios.get(
        `${api}${API_ENDPOINTS.trackedKeywords}`,
        { headers: getAuthHeaders() }
      )
      const gameDataList = res.data?.data?.game_data || []
      const game = gameDataList.find(g => String(g.item_id) === String(item_id))
      setTrackedKeywordSummary(game || null)
    } catch (err) {
      console.error("Failed to fetch tracked app:", err)
    }
  }, [item_id])

  // Fetch app details
  const fetchAppDetails = useCallback(async () => {
    if (!item_id) return

    setNotFoundError(false)
    try {
      const res = await axios.get(`${api}${API_ENDPOINTS.appDetails}${item_id}`)

      // Check if details exists and is not an empty object
      if (res.data.status === 200 && res.data.details && Object.keys(res.data.details).length > 0) {
        setTrackedApp(res.data.details)
        setNotFoundError(false)
      } else {
        // Handle empty details or other status codes as Not Found
        setTrackedApp(null)
        setNotFoundError(true)
      }
    } catch (err) {
      console.error("Failed to fetch app details:", err)
      // Treat any error as Not Found to avoid infinite loading
      setNotFoundError(true)
      setTrackedApp(null)
    }
  }, [item_id])

  // Fetch keyword tracking data
  const fetchKeywordTracking = useCallback(async (selectedCountry, selectedLanguage, fromDate, toDate) => {
    if (!item_id) {
      setKeywordTrackingData(null)
      setGameData([])
      return null
    }

    try {
      setKeywordsLoading(true)
      // Create params object with item_id
      const params = { item_id }

      // Only add country and language params if they are provided
      if (selectedCountry) params.country = getCountryCode(selectedCountry) || selectedCountry
      if (selectedLanguage) params.language = selectedLanguage

      // Add date parameters if provided
      if (fromDate) {
        const formattedFromDate = fromDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        params.start_date = formattedFromDate;
      }
      if (toDate) {
        const formattedToDate = toDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        params.end_date = formattedToDate;
      }

      const res = await axios.get(
        `${api}${API_ENDPOINTS.keywordTracking}`,
        {
          params,
          headers: getAuthHeaders(),
        }
      )

      if (res.data.status === 200) {
        // For calls with country/language, response structure is different
        // than calls without country/language
        if (selectedCountry && selectedLanguage) {
          setKeywordTrackingData(res.data.data)
          setGameData(res.data.data.game_data || [])
          setKeywordsLoading(false)
          return res.data.data
        } else {
          // For initial call without country/language, return the data object directly
          // which contains the keywords
          setKeywordsLoading(false)
          return res.data
        }
      } else {
        setKeywordTrackingData(null)
        setGameData([])
        setKeywordsLoading(false)
        return null
      }
    } catch (err) {
      console.error("Keyword tracking API error:", err)
      setKeywordTrackingData(null)
      setGameData([])
      setKeywordsLoading(false)
      return null
    }
  }, [item_id])

  // Add keyword tracking
  // Add keyword tracking
  const addKeywordTracking = useCallback(async (keyword, selectedCountry, selectedLanguage, fromDate, toDate, onPollingTimeout) => {
    if (!keyword || addingKeyword || !selectedCountry || !selectedLanguage) return null;

    setAddingKeyword(true);

    // Optimistically add a placeholder row so the table updates immediately
    try {
      setKeywordTrackingData(prev => {
        const placeholder = {
          // Minimal shape expected by table code
          country: selectedCountry,
          language: selectedLanguage,
          has_data: false,
          _loading: true,
        };

        // If no previous data, start with an object
        const base = (prev && typeof prev === 'object') ? { ...prev } : {};

        // Preserve existing game_data if present
        if (prev && prev.game_data) {
          base.game_data = prev.game_data;
        }

        // Only add if not already present
        if (!base[keyword]) {
          base[keyword] = placeholder;
        }
        return base;
      });
    } catch (optErr) {
      console.warn('Optimistic update failed, proceeding without it:', optErr);
    }
    try {
      const requestData = {
        item_id,
        keyword,
        country: getCountryCode(selectedCountry) || selectedCountry, // Convert name to code
        language: selectedLanguage,
      };

      const res = await axios.post(
        `${api}${API_ENDPOINTS.addKeywordTracking}`,
        requestData,
        { headers: getPostHeaders() }
      );

      // If server signals upgrade required (HTTP 200 but status:400 in body), remove placeholder
      if (res.data?.status !== 200) {
        setKeywordTrackingData(prev => {
          if (!prev || typeof prev !== 'object') return prev;
          const next = { ...prev };
          const gameDataSnapshot = next.game_data;
          delete next[keyword];
          if (gameDataSnapshot) next.game_data = gameDataSnapshot;
          return next;
        });
      }

      // Always return raw response so caller can decide (success OR upgrade)
      if (res.data?.status === 200) {
        // Silent refresh: fetch updated tracking data WITHOUT toggling the global loading state
        try {
          const params = { item_id, country: getCountryCode(selectedCountry) || selectedCountry, language: selectedLanguage };
          if (fromDate) {
            const formattedFromDate = fromDate.toISOString().split('T')[0];
            params.start_date = formattedFromDate;
          }
          if (toDate) {
            const formattedToDate = toDate.toISOString().split('T')[0];
            params.end_date = formattedToDate;
          }

          const refresh = await axios.get(
            `${api}${API_ENDPOINTS.keywordTracking}`,
            {
              params,
              headers: getAuthHeaders(),
            }
          );

          if (refresh.data?.status === 200) {
            // Update state directly without touching keywordsLoading
            setKeywordTrackingData(prev => {
              const incoming = refresh.data.data || {};
              const merged = { ...(prev || {}), ...incoming };
              if (merged[keyword] && merged[keyword].has_data) {
                merged[keyword]._loading = false;
              }
              // preserve game_data if present
              if (incoming.game_data) merged.game_data = incoming.game_data;
              return merged;
            });
            setGameData(refresh.data.data.game_data || []);

            // If this keyword still lacks data, start polling for up to 30 seconds
            const entry = refresh.data.data[keyword];
            const needsPolling = !(entry && entry.has_data);
            if (needsPolling) {
              const deadline = Date.now() + 60000; // 60s
              const poll = async () => {
                if (Date.now() > deadline) {
                  setKeywordTrackingData(prev => {
                    const base = { ...(prev || {}) };
                    if (base[keyword]) base[keyword]._loading = false;
                    return base;
                  });
                  onPollingTimeout?.();
                  return;
                }
                try {
                  const again = await axios.get(
                    `${api}${API_ENDPOINTS.keywordTracking}`,
                    {
                      params,
                      headers: getAuthHeaders(),
                    }
                  );
                  if (again.data?.status === 200) {
                    const d = again.data.data || {};
                    const e = d[keyword];
                    if (e && e.has_data) {
                      setKeywordTrackingData(prev => {
                        const next = { ...(prev || {}) };
                        next[keyword] = { ...e, _loading: false };
                        if (d.game_data) next.game_data = d.game_data;
                        return next;
                      });
                      setGameData(d.game_data || []);
                      return;
                    }
                  }
                } catch (_) {
                  // ignore and continue polling
                }
                setTimeout(poll, 10000);
              };
              setTimeout(poll, 10000);
            }
          }
        } catch (silentErr) {
          console.error("Silent refresh after addKeywordTracking failed:", silentErr);
          // Intentionally do not change keywordsLoading here to avoid table flash
        }
      }

      return res.data; // ✅ return the entire payload
    } catch (err) {
      console.error("addKeywordTracking - API error:", err);
      // Remove optimistic placeholder since the keyword was not added
      setKeywordTrackingData(prev => {
        if (!prev || typeof prev !== 'object') return prev;
        const next = { ...prev };
        const gameDataSnapshot = next.game_data;
        delete next[keyword];
        if (gameDataSnapshot) next.game_data = gameDataSnapshot;
        return next;
      });
      return err.response?.data || { status: 500, message: "Unknown error" };
    } finally {
      setAddingKeyword(false);
    }
  }, [item_id, addingKeyword]);


  // Remove entity (keyword or entire app or category)
  const removeEntity = useCallback(async (entityId, entityType = "keyword", options = {}) => {
    try {
      const res = await axios.post(
        `${api}${API_ENDPOINTS.removeEntity}`,
        {
          game_id: item_id,
          entity_type: entityType,
          entity_id: entityId || null,
          // Backend expects country code (e.g. "AU"), not full name ("Australia")
          country: getCountryCode(options.country) || options.country,
          language: options.language,
        },
        { headers: getPostHeaders() }
      )

      const ok = res?.data?.status === 200
      if (ok && entityId) {
        // Optimistically remove from keywordTrackingData without toggling loading
        setKeywordTrackingData(prev => {
          if (!prev || typeof prev !== 'object') return prev
          const next = { ...prev }
          // Preserve game_data
          const gameDataSnapshot = next.game_data
          delete next[entityId]
          if (gameDataSnapshot) next.game_data = gameDataSnapshot
          return next
        })
      }
      return ok
    } catch (err) {
      console.error("Remove entity error:", err)
      return false
    }
  }, [item_id])

  // Initialize data on mount
  useEffect(() => {
    fetchCountries();
    fetchPredefinedKeywords();
    fetchTrackedApp();
  }, [fetchCountries, fetchPredefinedKeywords, fetchTrackedApp])

  useEffect(() => {
    fetchAppDetails()
  }, [fetchAppDetails])

  return {
    // State
    countriesData,
    predefinedKeywords,
    keywordsLoading,
    trackedApp,
    trackedKeywordSummary,
    keywordTrackingData,
    gameData,
    addingKeyword,
    notFoundError,

    // Actions
    fetchKeywordTracking,
    addKeywordTracking,
    removeEntity,
    fetchPredefinedKeywords,
  }
} 